import { useSyncExternalStore } from "react";

/**
 * Per-browser Focus preferences (`localStorage["dux-focus:prefs"]`).
 *
 * The Mac menu-bar app is the primary notifier, so browser notifications and
 * the chime default to off; the tab-title countdown defaults to on. One
 * module-level store keeps the timer chip (which acts on the prefs) and the
 * Today view (which toggles them) in step, and the `storage` event carries a
 * toggle into the other open tabs.
 */
export interface FocusPrefs {
  notify: boolean;
  sound: boolean;
  titleCountdown: boolean;
}

const KEY = "dux-focus:prefs";
const DEFAULTS: FocusPrefs = { notify: false, sound: false, titleCountdown: true };

let cached: FocusPrefs | null = null;
const listeners = new Set<() => void>();

function read(): FocusPrefs {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<FocusPrefs>;
    return {
      notify: typeof parsed.notify === "boolean" ? parsed.notify : DEFAULTS.notify,
      sound: typeof parsed.sound === "boolean" ? parsed.sound : DEFAULTS.sound,
      titleCountdown:
        typeof parsed.titleCountdown === "boolean" ? parsed.titleCountdown : DEFAULTS.titleCountdown,
    };
  } catch {
    return DEFAULTS;
  }
}

function getSnapshot(): FocusPrefs {
  if (cached === null) cached = read();
  return cached;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== KEY) return;
    cached = read();
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function setFocusPrefs(patch: Partial<FocusPrefs>): void {
  cached = { ...getSnapshot(), ...patch };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(cached));
  } catch {
    // Private mode / quota — the in-memory value still applies to this tab.
  }
  for (const listener of listeners) listener();
}

export function useFocusPrefs(): [FocusPrefs, (patch: Partial<FocusPrefs>) => void] {
  const prefs = useSyncExternalStore(subscribe, getSnapshot);
  return [prefs, setFocusPrefs];
}
